import { useContext, useEffect, useState } from "react";
import {
  getWarteliste,
  getMitglieder,
  getStandorteWithKurse,
  getBelegbareKurse,
  getAllStandortNamen,
  getMitgliedByID,
  deleteKunde,
  deleteStandort,
} from "../../api/admin-management-api";
import {
  ACTIVE_MAHNUNG,
  AdminMahnungFilterTypes,
  AdminManagementTabTypes,
  ALL_MAHNUNG,
  IMPORT_MAHNUNG,
  COMPLETED_MAHNUNG,
  INKASSO_MAHNUNG,
  MAHNUNG_TAB,
  MITGLIEDER_TAB,
  STANDORTE_TAB,
  TRAINER_TAB,
  WARTELISTE_TAB,
} from "../../constants/admin-management-constants";
import { dateToString } from "../../util/DateFormatHelper";
import { localStorageKeys } from "../../constants/storage-keys";
import {
  IdAndDataType,
  Kurs,
  Mitglied,
  SearchObject,
  SearchTypes,
  Standort,
  Warteliste,
  BelegbareKurseResponse,
  MahnungStatusTitleMap,
  MahnungStatus,
  Mahnung,
  AdminManagementDialogKey,
} from "../../models/admin-management-entities";
import AdminPortalHeader from "../../components/AdminHeader/AdminPortalHeader";
import AdminManagementSwitcher from "../../components/AdminManagement/AdminManagementSwitcher";
import LoadingScreen from "../../components/LoadingScreen";
import { AdminManagmentList } from "../../components/AdminManagement/AdminManagementList";
import { Box, Button, Stack, TextField, ThemeProvider } from "@mui/material";
import { AdminManagementMitgliedDialog } from "../../components/AdminManagement/Dialogs/AdminManagementMitgliedDialog";
import { AdminManagementStandortDialog } from "../../components/AdminManagement/Dialogs/AdminManagementStandortDialog";
import {
  AdminManagmentSearchPanel,
  managementSearchPanelBtnStyle,
} from '../../components/AdminManagement/AdminManagmentSearchPanel';
import { AdminManagmentWartelisteDialog } from "../../components/AdminManagement/Dialogs/AdminManagementWartelisteDialog";
import { setLocalStorageManagementActiveTab } from "../../util/StorageUtil";
import { AdminManagementKursDialog } from "../../components/AdminManagement/Dialogs/AdminManagementKursDialog";
import { customTheme } from "../../components/customTheme";
import SnackbarMessage from "../../components/Snackbar/SnackbarMessage";
import { SnackBarContext } from "../../context/snackbar-context";
import { HttpStatusCode } from "axios";
import { getAdminManagementTrainerEntities } from "../../api/trainer-api";
import {
  changeTrainerPassword,
  deactivateTrainer,
  reactivateTrainer,
} from "../../api/auth-api";
import { TrainerInterface } from "../../models/trainer";
import AdminInfoDialog from "../../components/AdminTrainerApp/Dialogs/AdminInfoDialog";
import userLock from "../../img/userLock.svg";
import userCheck from "../../img/userCheck.svg";
import password from "../../img/password.svg";
import AdminMahnungDialog from "../../components/AdminManagement/Dialogs/AdminMahnungDialog";
import AdminMahnungSwitcher from "../../components/AdminManagement/AdminMahnungSwitcher";
import AdminCsvUpload from "../../components/AdminManagement/AdminCsvUpload";
import {
  getManyMahnungen,
  searchMahnungen,
} from "../../api/admin-mahnwesen-api";
import { formatToGermanPrice } from "../../util/PriceUtil";
import AdminInkassoDialog from "../../components/AdminManagement/Dialogs/AdminInkassoDialog";
import AdminInkassoDownload from "../../components/AdminManagement/AdminInkassoDownload";
import { syncKurse, syncStandorte } from '../../api/admin-trainer-app-api';

const AdminManagementPage: React.FC = () => {
  const checkLocalStorageActiveManagementTab = () => {
    const managementActiveTab = localStorage.getItem(
      localStorageKeys.adminManagementActiveTab
    );
    if (!managementActiveTab) {
      return MITGLIEDER_TAB;
    }
    const activeTabNum = Number(managementActiveTab);
    switch (activeTabNum) {
      case 0:
        return STANDORTE_TAB;
      case 1:
        return MITGLIEDER_TAB;
      case 2:
        return WARTELISTE_TAB;
      case 3:
        return TRAINER_TAB;
      case 4:
        return MAHNUNG_TAB;
      default:
        return MITGLIEDER_TAB;
    }
  };

  const statusMap: Record<string, number[] | undefined> = {
    [ACTIVE_MAHNUNG]: [MahnungStatus.ACTIVE],
    [COMPLETED_MAHNUNG]: [MahnungStatus.STOPPED, MahnungStatus.PAID],
    [IMPORT_MAHNUNG]: [MahnungStatus.IMPORT],
    [INKASSO_MAHNUNG]: [MahnungStatus.INKASSO],
  };

  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const [activeTab, setActiveTab] = useState<AdminManagementTabTypes>(
    checkLocalStorageActiveManagementTab()
  );
  const [searchObject, setSearchObject] = useState<SearchObject>(undefined);

  const [standorteOffset, setStandorteOffset] = useState(0);
  const [standortData, setStandortData] = useState<Standort[]>([]);
  const [allStandortNamen, setAllStandortNamen] = useState<IdAndDataType[]>();

  const [mitgliederOffset, setMitgliederOffset] = useState(0);
  const [mitgliederData, setMitgliederData] = useState<Mitglied[]>([]);

  const [wartelisteOffset, setWartelisteOffset] = useState(0);
  const [wartelisteData, setWartelisteData] = useState<Warteliste[]>([]);

  const [trainerOffset, setTrainerOffset] = useState(0);
  const [trainerData, setTrainerData] = useState<TrainerInterface[]>([]);

  const [mitgliedDialogEntity, setMitgliedDialogEntity] =
    useState<Mitglied>(undefined);
  const [isMitgliedDialogOpen, setIsMitgliedDialogOpen] =
    useState<boolean>(false);
  const [standortDialogEntity, setStandortDialogEntity] =
    useState<Standort>(undefined);
  const [trainerDialogEntity, setTrainerDialogEntity] =
    useState<TrainerInterface>(undefined);
  const [isStandortDialogOpen, setIsStandortDialogOpen] =
    useState<boolean>(false);
  const [kursDialogEntity, setKursDialogEntity] = useState<Kurs>(undefined);
  const [wartelisteDialogEntity, setWartelisteDialogEntity] =
    useState<Warteliste>(undefined);

  const [isAddingNewItem, setIsAddingNewItem] = useState<boolean>(false);
  const [isActivateTrainerDialogOpen, setIsActivateTrainerDialogOpen] =
    useState<boolean>(false);
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState<boolean>(false);
  const [belegbareKurse, setBelegbareKurse] =
    useState<BelegbareKurseResponse>();

  const [newPassword, setNewPassword] = useState("");

  /* Mahnung */
  const [mahnungOffset, setMahnungOffset] = useState(0);
  const [mahnungData, setMahnungData] = useState<Mahnung[]>();
  const [isMahnungDialogOpen, setIsMahnungDialogOpen] = useState(false);
  const [mahnungDialogEntity, setMahnnugDialogEntity] = useState<Mahnung>();
  const [isInkassoDialogOpen, setIsInkassoDialogOpen] = useState(false);
  const [mahnungFilterTab, setMahnungFilterTab] =
    useState<AdminMahnungFilterTypes>(ALL_MAHNUNG);
  const [showMahnungLoadMoreBtn, setShowMahnungLoadMoreBtn] = useState(true);

  const snackBarCtx = useContext(SnackBarContext);

  const startLoading = () => {
    setIsLoading(true);
    setShowContent(false);
  };

  const stopLoading = () => {
    setIsLoading(false);
    setShowContent(true);
  };

  /* eslint-disable */

  useEffect(() => {
    startLoading();
    Promise.all([
      loadMitgliederData(mitgliederOffset),
      loadStandortData(standorteOffset),
      loadWartelisteData(wartelisteOffset),
      loadVerfugbareKurse(),
      loadAllStandortNamen(),
      loadTrainerData(trainerOffset),
      loadMahnungData(mahnungOffset),
    ]).then(() => {
      stopLoading();
    });
  }, []);

  useEffect(() => {
    reloadMahnungData();
  }, [mahnungFilterTab]);

  /* eslint-enable */

  const loadVerfugbareKurse = async () => {
    await Promise.all([
      getBelegbareKurse().then((returnValue: BelegbareKurseResponse) => {
        setBelegbareKurse(returnValue);
      }),
    ]);
  };

  const loadAllStandortNamen = async () => {
    await Promise.all([
      getAllStandortNamen().then((data: IdAndDataType[]) => {
        setAllStandortNamen(data);
      }),
    ]);
  };

  const loadMitgliederData = async (
    offset: number,
    searchQuery: string = ""
  ) => {
    setMitgliederOffset(offset);
    await Promise.all([
      getMitglieder(mitgliederOffset, searchQuery).then((mData: Mitglied[]) => {
        setMitgliederData(mData);
      }),
    ]);
  };

  const loadMoreMitglieder = async () => {
    setIsLoading(true);
    const newOffset = mitgliederOffset + 50;
    const { query } = searchObject || { query: "" };
    const mitgliedData: Mitglied[] = await getMitglieder(newOffset, query);
    setMitgliederData((prevMitglieder: Mitglied[]) => {
      const existingIDs = new Set(
        prevMitglieder.map((mitglied) => mitglied.ID)
      );
      const newUniqueMitglieder = mitgliedData.filter(
        (mitglied) => !existingIDs.has(mitglied.ID)
      );
      return [...prevMitglieder, ...newUniqueMitglieder];
    });
    setMitgliederOffset(newOffset);
    setIsLoading(false);
  };

  const loadStandortData = async (offset: number, searchQuery: string = "") => {
    setStandorteOffset(offset);
    await Promise.all([
      getStandorteWithKurse(offset, searchQuery).then((data: Standort[]) => {
        setStandortData(data);
        loadAllStandortNamen();
      }),
    ]);
  };

  const activateBtnText =
    trainerDialogEntity && trainerDialogEntity.isDeactivated
      ? "aktivieren"
      : "deaktivieren";

  const activateIcon =
    trainerDialogEntity && trainerDialogEntity.isDeactivated
      ? userCheck
      : userLock;

  const loadMoreStandorte = async () => {
    setIsLoading(true);
    const { query } = searchObject || { query: "" };
    const newOffset = standorteOffset + 50;
    const newStandorte = await getStandorteWithKurse(newOffset, query);
    setStandortData((prevMitglieder: any) => {
      return [...prevMitglieder, ...newStandorte];
    });
    setStandorteOffset(newOffset);
    setIsLoading(false);
  };

  const loadWartelisteData = async (
    offset: number,
    searchQuery: string = ""
  ) => {
    setWartelisteOffset(offset);
    await Promise.all([
      getWarteliste(offset, searchQuery).then((data: Warteliste[]) => {
        setWartelisteData(data);
      }),
    ]);
  };

  const loadTrainerData = async (offset: number, searchQuery: string = "") => {
    setTrainerOffset(offset);
    await Promise.all([
      getAdminManagementTrainerEntities(searchQuery, offset).then((data) => {
        setTrainerData(data.trainers);
      }),
    ]);
  };

  const loadMoreTrainer = async () => {
    setIsLoading(true);
    const newOffset = trainerOffset + 50;
    const { query } = searchObject || { query: "" };
    const trainerData = await getAdminManagementTrainerEntities(
      query,
      newOffset
    );
    setTrainerData((prevTrainer: TrainerInterface[]) => {
      const existingUUIDs = new Set(prevTrainer.map((trainer) => trainer.uuid));
      const newUniqueTrainer = trainerData.trainers.filter(
        (trainer) => !existingUUIDs.has(trainer.uuid)
      );
      return [...prevTrainer, ...newUniqueTrainer];
    });
    setTrainerOffset(newOffset);
    setIsLoading(false);
  };

  const loadMahnungData = async (offset: number, mahnungStatus?: number[]) => {
    const { status, mahnungen } = await getManyMahnungen(offset, mahnungStatus);
    if (status !== 200) {
      return; // Error;
    }
    setMahnungData(mahnungen);
  };

  const searchMahnung = async (searchQuery: string) => {
    if (!searchQuery) {
      return;
    }
    const { mahnungen } = await searchMahnungen(searchQuery);
    setMahnungData(mahnungen);
  };

  const reloadMahnungData = async () => {
    await loadMahnungData(0, statusMap[mahnungFilterTab]);
  };

  const loadMoreMahnungen = async () => {
    setIsLoading(true);
    const newOffset = mahnungData.length;
    const { query } = searchObject || { query: "" };

    let newMahnungen: Mahnung[] = [];

    if (searchObject?.type === SearchTypes.MAHNUNG && query) {
      const { mahnungen } = await searchMahnungen(query, newOffset);
      newMahnungen = mahnungen;
    } else {
      const { status, mahnungen } = await getManyMahnungen(
        newOffset,
        statusMap[mahnungFilterTab]
      );
      if (status !== 200) {
        setIsLoading(false);
        return;
      }
      newMahnungen = mahnungen;
    }

    if (newMahnungen.length === 0) {
      snackBarCtx.openSnackbar("Alle Mahnungen geladen", "success");
      setShowMahnungLoadMoreBtn(false);
    }

    setMahnungData((prev: Mahnung[]) => {
      const existingIds = new Set(prev.map((m) => m.ID));
      const newUnique = newMahnungen.filter((m) => !existingIds.has(m.ID));
      return [...prev, ...newUnique];
    });

    setMahnungOffset(newOffset);
    setIsLoading(false);
  };

  const executeSearchHandler = async (
    searchType: SearchTypes,
    searchQuery: string
  ) => {
    const resetOffset = 0;
    setSearchObject({
      type: searchType,
      query: searchQuery,
    });
    startLoading();
    if (searchType === SearchTypes.MITGLIED) {
      await loadMitgliederData(resetOffset, searchQuery);
      stopLoading();
    }
    if (searchType === SearchTypes.STANDORT) {
      await loadStandortData(resetOffset, searchQuery);
      stopLoading();
    }
    if (searchType === SearchTypes.WARTELISTE) {
      await loadWartelisteData(resetOffset, searchQuery);
      stopLoading();
    }
    if (searchType === SearchTypes.TRAINER) {
      await loadTrainerData(resetOffset, searchQuery);
      stopLoading();
    }
    if (searchType === SearchTypes.MAHNUNG) {
      setMahnungFilterTab("ALL");
      await searchMahnung(searchQuery);
      stopLoading();
    }
  };

  const handleSyncStandorte = async () => {
    try {
      await syncStandorte();
      snackBarCtx.openSnackbar(
        "Standorte wurden erfolgreich synchronisiert!",
        "success"
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      snackBarCtx.openSnackbar(
        "Standorte konnten nicht synchronisiert werden: " + errorMessage,
        "error"
      );
    }
  };

  const handleSyncKurse = async () => {
    try {
      await syncKurse();
      snackBarCtx.openSnackbar(
        "Kurse wurden erfolgreich synchronisiert!",
        "success"
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      snackBarCtx.openSnackbar(
        "Kurse konnten nicht synchronisiert werden: " + errorMessage,
        "error"
      );
    }
  };

  function getSearchAndFilterPanel() {
    if (activeTab === MITGLIEDER_TAB) {
      return (
        <AdminManagmentSearchPanel
          searchQuery={searchObject ? searchObject.query : ""}
          setSearchQuery={(searchQuery: string) => {
            setSearchObject({
              type: SearchTypes.MITGLIED,
              query: searchQuery,
            });
          }}
          onExecuteSearch={async (searchQuery: string) => {
            await executeSearchHandler(SearchTypes.MITGLIED, searchQuery);
          }}
          onResetSearch={async () => {
            setSearchObject(undefined);
            startLoading();
            await loadMitgliederData(0);
            stopLoading();
          }}
          children={
            <>
              <Button
                onClick={() => {
                  setIsAddingNewItem(true);
                  setMitgliedDialogEntity({} as Mitglied);
                  setIsMitgliedDialogOpen(true);
                }}
                sx={managementSearchPanelBtnStyle}
              >
                Mitglied hinzufügen
              </Button>
            </>
          }
        />
      );
    } else if (activeTab === STANDORTE_TAB) {
      return (
        <AdminManagmentSearchPanel
          searchQuery={searchObject ? searchObject.query : ""}
          setSearchQuery={(searchQuery: string) => {
            setSearchObject({
              type: SearchTypes.STANDORT,
              query: searchQuery,
            });
          }}
          onExecuteSearch={async (searchQuery: string) => {
            await executeSearchHandler(SearchTypes.STANDORT, searchQuery);
          }}
          onResetSearch={async () => {
            setSearchObject(undefined);
            startLoading();
            await loadStandortData(0);
            stopLoading();
          }}
          children={
            <>
              <Button
                onClick={() => {
                  setIsAddingNewItem(true);
                  setStandortDialogEntity({} as Standort);
                  setIsStandortDialogOpen(true);
                }}
                sx={managementSearchPanelBtnStyle}
              >
                Standort hinzufügen
              </Button>
              <Button
                onClick={() => {
                  setIsAddingNewItem(true);
                  setKursDialogEntity({} as Kurs);
                }}
                sx={managementSearchPanelBtnStyle}
              >
                Kurs hinzufügen
              </Button>
              <Button
                onClick={handleSyncStandorte}
                sx={managementSearchPanelBtnStyle}>
                Standorte synchronisieren
              </Button>
              <Button
                onClick={handleSyncKurse}
                sx={managementSearchPanelBtnStyle}>
                Kurse synchronisieren
              </Button>
            </>
          }
        />
      );
    } else if (activeTab === TRAINER_TAB) {
      return (
        <AdminManagmentSearchPanel
          searchQuery={searchObject ? searchObject.query : ""}
          setSearchQuery={(searchQuery: string) => {
            setSearchObject({
              type: SearchTypes.TRAINER,
              query: searchQuery,
            });
          }}
          onExecuteSearch={async (searchQuery: string) => {
            await executeSearchHandler(SearchTypes.TRAINER, searchQuery);
          }}
          onResetSearch={async () => {
            setSearchObject(undefined);
            startLoading();
            await loadStandortData(0);
            stopLoading();
          }}
        />
      );
    } else if (activeTab === MAHNUNG_TAB) {
      return (
        <AdminManagmentSearchPanel
          searchQuery={searchObject ? searchObject.query : ""}
          setSearchQuery={(searchQuery: string) => {
            setSearchObject({
              type: SearchTypes.MAHNUNG,
              query: searchQuery,
            });
          }}
          onExecuteSearch={async (searchQuery: string) => {
            await executeSearchHandler(SearchTypes.MAHNUNG, searchQuery);
          }}
          onResetSearch={async () => {
            setSearchObject(undefined);
            startLoading();
            setMahnungFilterTab("ALL");
            await loadMahnungData(0);
            stopLoading();
          }}
          children={
            <>
              <AdminMahnungSwitcher
                activeTab={mahnungFilterTab}
                onChangeTab={(newTab: AdminMahnungFilterTypes) => {
                  setMahnungFilterTab(newTab);
                  setMahnungOffset(0);
                  setShowMahnungLoadMoreBtn(true);
                }}
              ></AdminMahnungSwitcher>
              <AdminInkassoDownload
                onOpenDialog={() => {
                  setIsInkassoDialogOpen(true);
                }}
              ></AdminInkassoDownload>
              <AdminCsvUpload
                onImportCsv={async () => {
                  startLoading();
                  setMahnungFilterTab("IMPORT");
                  await loadMahnungData(0, [MahnungStatus.IMPORT]);
                  stopLoading();
                }}
              />
            </>
          }
        />
      );
    } else {
      return (
        <AdminManagmentSearchPanel
          searchQuery={searchObject ? searchObject.query : ""}
          setSearchQuery={(searchQuery: string) => {
            setSearchObject({
              type: SearchTypes.WARTELISTE,
              query: searchQuery,
            });
          }}
          onExecuteSearch={async (searchQuery: string) => {
            await executeSearchHandler(SearchTypes.WARTELISTE, searchQuery);
          }}
          onResetSearch={async () => {
            setSearchObject(undefined);
            startLoading();
            await loadWartelisteData(0);
            stopLoading();
          }}
        />
      );
    }
  }

  return (
    <ThemeProvider theme={customTheme}>
      <AdminPortalHeader
        headline="Verwaltung"
        setAdminManagementActiveTab={(activeTab: AdminManagementTabTypes) => {
          setActiveTab(activeTab);
        }}
      ></AdminPortalHeader>
      <AdminManagementSwitcher
        activeTab={activeTab}
        onChangeTab={(newTab: AdminManagementTabTypes) => {
          setLocalStorageManagementActiveTab(newTab);
          setActiveTab(newTab);
          setSearchObject(undefined);
        }}
      ></AdminManagementSwitcher>
      <LoadingScreen isLoading={isLoading}></LoadingScreen>
      {getSearchAndFilterPanel()}
      {showContent && (
        <>
          {activeTab === STANDORTE_TAB && (
            <Box sx={{ width: "90%", margin: "auto" }}>
              <AdminManagmentList
                dialogKey={AdminManagementDialogKey.STANDORT}
                setDialogEntityData={setStandortDialogEntity}
                isLoading={isLoading}
                data={standortData}
                loadMoreDataFct={async () => {
                  await loadMoreStandorte();
                }}
                columns={[
                  { title: "Standortname", flex: 3 },
                  { title: "Straße", flex: 2 },
                  { title: "PLZ", flex: 2 },
                  { title: "Ort", flex: 2 },
                  { title: "Ansprechpartner", flex: 2 },
                  { title: "Kontakt", flex: 2 },
                  { title: "", flex: 1 },
                ]}
                listItemOnClick={(ID: number) => {
                  setStandortDialogEntity(
                    standortData.find((standort) => standort.ID === ID)
                  );
                }}
                listItemColumns={[
                  { attrName: "Standortname", flex: 3 },
                  { attrName: "Strasse", flex: 2 },
                  { attrName: "PLZ", flex: 2 },
                  { attrName: "Ort", flex: 2 },
                  { attrName: "Ansprechpartner", flex: 2 },
                  { attrName: "Kontakt", flex: 2 },
                ]}
                noDataText={"Keine Standorte gefunden."}
                deleteDialogTextContent={
                  <>
                    Möchtest du den Standort{" "}
                    <b>
                      {standortDialogEntity &&
                        standortDialogEntity.Standortname}
                    </b>
                    <br /> wirklich unwiderruflich löschen ?
                  </>
                }
                currentItem={standortDialogEntity}
                deleteFnct={async (idStandort: number) => {
                  await deleteStandort(idStandort, snackBarCtx)
                    .then((response) => {
                      if (response.status === HttpStatusCode.Ok) {
                        loadStandortData(0);
                        snackBarCtx.openSnackbar(
                          "Standort wurde erfolgreich gelöscht!",
                          "success"
                        );
                      }
                    })
                    .catch((response) => {
                      snackBarCtx.openSnackbar(
                        "Standort kann nicht gelöscht werden: " +
                          response.message,
                        "error"
                      );
                    });
                }}
                setIsEntityDialogOpen={setIsStandortDialogOpen}
              />
            </Box>
          )}
          {activeTab === MITGLIEDER_TAB && (
            <Box sx={{ width: "90%", margin: "auto" }}>
              <AdminManagmentList
                dialogKey={AdminManagementDialogKey.MITGLIED}
                setDialogEntityData={setMitgliedDialogEntity}
                isLoading={isLoading}
                data={mitgliederData}
                loadMoreDataFct={async () => {
                  await loadMoreMitglieder();
                }}
                columns={[
                  { title: "ID", flex: 2 },
                  { title: "Name", flex: 2 },
                  { title: "Vorname", flex: 2 },
                  { title: "Geburtsdatum", flex: 2 },
                  { title: "E-Mail", flex: 3 },
                  { title: "Hinzugefügt", flex: 2 },
                  { title: "Mitgliedschaftsbeginn", flex: 2 },
                  { title: "", flex: 1 },
                ]}
                listItemOnClick={(ID: number) => {
                  setMitgliedDialogEntity(
                    mitgliederData.find((member) => member.ID === ID)
                  );
                }}
                listItemColumns={[
                  { attrName: "ID", flex: 2 },
                  { attrName: "Nachname", flex: 2 },
                  { attrName: "Vorname", flex: 2 },
                  {
                    attrName: "Geburtsdatum",
                    processFnct: (_: Date) => {
                      return dateToString(_);
                    },
                    flex: 2,
                  },
                  { attrName: "Emailadresse", flex: 3 },
                  {
                    attrName: "Hinzugefuegt",
                    processFnct: (_: Date) => {
                      return dateToString(_);
                    },
                    flex: 2,
                  },
                  {
                    attrName: "Mitgliedschaftsbeginn",
                    processFnct: (_: Date) => {
                      return dateToString(_);
                    },
                    flex: 2,
                  },
                ]}
                noDataText={"Keine Mitglieder gefunden."}
                deleteDialogTextContent={
                  <>
                    {"Bist du sicher, dass du das Mitglied "}
                    <br />
                    <b>
                      {`${mitgliedDialogEntity && mitgliedDialogEntity.Vorname} 
                      ${mitgliedDialogEntity && mitgliedDialogEntity.Nachname}`}
                    </b>
                    <br />
                    {"endgültig löschen möchtest?"}
                  </>
                }
                currentItem={mitgliedDialogEntity}
                deleteFnct={async () => {
                  await deleteKunde(mitgliedDialogEntity.ID)
                    .then((response) => {
                      if (response.status === HttpStatusCode.Ok) {
                        loadMitgliederData(0);
                        snackBarCtx.openSnackbar(
                          "Mitglied wurde erfolgreich gelöscht!",
                          "success"
                        );
                      }
                    })
                    .catch((response) => {
                      snackBarCtx.openSnackbar(
                        "Mitglied kann nicht gelöscht werden: " +
                          response.message,
                        "error"
                      );
                    });
                }}
                setIsEntityDialogOpen={setIsMitgliedDialogOpen}
              />
            </Box>
          )}
          {activeTab === WARTELISTE_TAB && (
            <Box sx={{ width: "90%", margin: "auto" }}>
              <AdminManagmentList
                dialogKey={AdminManagementDialogKey.WARTELISTE}
                isLoading={isLoading}
                data={wartelisteData}
                loadMoreDataFct={async () => {}}
                columns={[
                  { title: "Kursname" },
                  { title: "Kursbezeichnung" },
                  { title: "Kapazität" },
                  { title: "Wartelisteneinträge" },
                ]}
                listItemOnClick={(ID: number) => {
                  setWartelisteDialogEntity(
                    wartelisteData.find((warteliste) => warteliste.ID === ID)
                  );
                }}
                listItemColumns={[
                  { attrName: "Kursname" },
                  {
                    attrName: "Kurzbezeichnung",
                  },
                  { attrName: "MaxTeilnehmer" },
                  {
                    attrName: "WartelistenKundenData",
                    processFnct: (_: any) => {
                      return _.length;
                    },
                  },
                ]}
                noDataText={"Keine Wartelisten gefunden."}
              />
            </Box>
          )}
          {activeTab === TRAINER_TAB && (
            <Box sx={{ width: "90%", margin: "auto" }}>
              <AdminManagmentList
                dialogKey={AdminManagementDialogKey.STANDORT}
                setDialogEntityData={setTrainerDialogEntity}
                isLoading={isLoading}
                data={trainerData && trainerData}
                loadMoreDataFct={async () => {
                  await loadMoreTrainer();
                }}
                columns={[
                  { title: "UUID", flex: 3 },
                  { title: "Vorname", flex: 1 },
                  { title: "Nachname", flex: 2 },
                  { title: "Email", flex: 3 },
                  { title: "Telefonnumer", flex: 2 },
                  { title: "Deaktiviert", flex: 0.75 },
                  { title: "", flex: 1 },
                ]}
                listItemOnClick={(ID: number) => {
                  setTrainerDialogEntity(
                    trainerData.find((trainer) => trainer.ID === ID)
                  );
                }}
                listItemColumns={[
                  { attrName: "uuid", flex: 3 },
                  { attrName: "firstName", flex: 1 },
                  { attrName: "lastName", flex: 2 },
                  { attrName: "email", flex: 3 },
                  { attrName: "phone", flex: 2 },
                  {
                    attrName: "isDeactivated",
                    processFnct: (_: any) => {
                      return _ ? "Ja" : "Nein";
                    },
                    flex: 0.75,
                  },
                ]}
                noDataText={"Keine Trainer gefunden."}
                currentItem={trainerDialogEntity}
                contextMenuItems={[
                  {
                    text:
                      activateBtnText.charAt(0).toUpperCase() +
                      activateBtnText.slice(1),
                    icon: activateIcon,
                    onClick: () => setIsActivateTrainerDialogOpen(true),
                  },
                  {
                    text: "Passwort ändern",
                    icon: password,
                    onClick: () => setIsChangePasswordDialogOpen(true),
                  },
                ]}
              />
            </Box>
          )}
          {activeTab === MAHNUNG_TAB && (
            <Box sx={{ width: "90%", margin: "auto" }}>
              <AdminManagmentList
                dialogKey={AdminManagementDialogKey.MAHNUNG}
                setDialogEntityData={setMahnnugDialogEntity}
                isLoading={isLoading}
                data={mahnungData}
                loadMoreDataFct={async () => {
                  await loadMoreMahnungen();
                }}
                columns={[
                  { title: "ID", flex: 1 },
                  { title: "ID_Belegung", flex: 1 },
                  { title: "ID_Kunde", flex: 1 },
                  { title: "Erstellt am", flex: 1 },
                  { title: "Name", flex: 2 },
                  { title: "Vorname", flex: 2 },
                  { title: "Mahnstufe", flex: 1 },
                  { title: "Frist", flex: 2 },
                  { title: "Status", flex: 2 },
                  { title: "Summe", flex: 1 },
                  { title: "Exportiert ?", flex: 1 },
                ]}
                listItemOnClick={(ID: number) => {
                  setMahnnugDialogEntity(mahnungData.find((m) => m.ID === ID));
                }}
                listItemColumns={[
                  { attrName: "ID", flex: 1 },
                  { attrName: "ID_Belegung", flex: 1 },
                  { attrName: "ID_Kunde", flex: 1 },
                  {
                    attrName: "createdDate",
                    processFnct: (_: Date) => {
                      return dateToString(_);
                    },
                    flex: 1,
                  },
                  {
                    attrName: "Nachname",
                    flex: 2,
                  },
                  { attrName: "Vorname", flex: 2 },
                  {
                    attrName: "Mahnstufe",
                    flex: 1,
                  },
                  {
                    attrName: "Mahnfrist",
                    processColor: (_: Date) => {
                      if (new Date(_) < new Date()) {
                        return "var(--red)";
                      }
                      return "var(--grey)";
                    },
                    processFnct: (_: Date) => {
                      return dateToString(_);
                    },
                    flex: 2,
                  },
                  {
                    attrName: "Status",
                    processFnct: (_: number) => {
                      return MahnungStatusTitleMap[
                        _ as keyof typeof MahnungStatusTitleMap
                      ];
                    },
                    flex: 2,
                  },
                  {
                    attrName: "Summe",
                    processFnct: (_: number) => {
                      return `${formatToGermanPrice(_)} €`;
                    },
                    flex: 1,
                  },
                  {
                    attrName: "IstExportiert",
                    processFnct: (_: boolean) => {
                      if (_) {
                        return "✓";
                      }
                      return "";
                    },
                    flex: 1,
                  },
                ]}
                noDataText={"Keine Mahnungen gefunden."}
                currentItem={mahnungDialogEntity}
                setIsEntityDialogOpen={setIsMahnungDialogOpen}
                changeBorderColor="var(--green)"
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                {showMahnungLoadMoreBtn &&
                  mahnungData &&
                  mahnungData.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        await loadMoreMahnungen();
                      }}
                      sx={{
                        padding: "6px 40px",
                        margin: "10px",
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        boxShadow: 2,
                        textTransform: "none",
                        color: "white",
                      }}
                    >
                      Mehr laden
                    </Button>
                  )}
              </Box>
            </Box>
          )}
        </>
      )}
      <AdminInfoDialog
        isOpen={isChangePasswordDialogOpen}
        onCloseDialog={() => setIsChangePasswordDialogOpen(false)}
        iconURL={password}
        textChildren={
          <Stack sx={{ gap: "1rem", alignItems: "center" }}>
            {trainerDialogEntity &&
              `Möchtest du das Passwort von Trainer:in ${trainerDialogEntity.firstName} ${trainerDialogEntity.lastName} wirklich ändern?`}
            <TextField
              sx={{ width: "70%" }}
              required
              label="Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Stack>
        }
        leftBtnLabel="Abbrechen"
        leftBtnOnClick={() => {
          setIsChangePasswordDialogOpen(false);
          setNewPassword("");
        }}
        rightBtnLabel="Ändern"
        rightBtnOnClick={async () => {
          if (newPassword !== "") {
            const response = await changeTrainerPassword(
              trainerDialogEntity.uuid,
              newPassword
            );
            if (response.status === HttpStatusCode.Created) {
              setIsChangePasswordDialogOpen(false);
              snackBarCtx.openSnackbar(
                `Das Passwort vom Trainer: ${trainerDialogEntity.firstName} ${trainerDialogEntity.lastName} wurde erfolgreich geändert.`,
                "success"
              );
              setNewPassword("");
            } else {
              snackBarCtx.openSnackbar(
                "Ein Fehler ist aufgetreten bitte versuche es erneut.",
                "error"
              );
            }
          }
        }}
      />
      <AdminInfoDialog
        isOpen={isActivateTrainerDialogOpen}
        onCloseDialog={() => setIsActivateTrainerDialogOpen(false)}
        iconURL={activateIcon}
        textChildren={
          trainerDialogEntity &&
          `Möchtest du Trainer:in ${trainerDialogEntity.firstName} ${trainerDialogEntity.lastName} wirklich ${activateBtnText}?`
        }
        leftBtnLabel="Abbrechen"
        leftBtnOnClick={() => {
          setIsActivateTrainerDialogOpen(false);
        }}
        rightBtnLabel={activateBtnText}
        rightBtnOnClick={async () => {
          if (trainerDialogEntity.isDeactivated) {
            await deactivateTrainer(trainerDialogEntity.uuid);
          } else {
            await reactivateTrainer(trainerDialogEntity.uuid);
          }
          setIsActivateTrainerDialogOpen(false);
          await loadTrainerData(trainerOffset);
        }}
      />
      {mahnungDialogEntity && (
        <AdminMahnungDialog
          isOpen={isMahnungDialogOpen}
          idMahnung={mahnungDialogEntity.ID_Mahnung}
          onCloseDialog={async () => {
            setIsMahnungDialogOpen(false);
            await reloadMahnungData();
          }}
        ></AdminMahnungDialog>
      )}
      <AdminManagementMitgliedDialog
        mitglied={mitgliedDialogEntity}
        setMitglied={setMitgliedDialogEntity}
        belegbareKurse={belegbareKurse}
        isOpen={isMitgliedDialogOpen}
        closeDialog={() => {
          setMitgliedDialogEntity(undefined);
          setIsMitgliedDialogOpen(false);
          setIsAddingNewItem(false);
        }}
        isAddingNewItem={isAddingNewItem}
        setIsAddingNewItem={setIsAddingNewItem}
        reloadData={async () => {
          await loadMitgliederData(0);
        }}
        reloadMitglied={async (idKunde: number) => {
          setMitgliedDialogEntity(undefined);
          startLoading();
          const updatedMitglied = await getMitgliedByID(idKunde);
          setMitgliedDialogEntity(updatedMitglied);
          stopLoading();
        }}
      ></AdminManagementMitgliedDialog>
      <AdminManagementStandortDialog
        allStandortNamen={allStandortNamen}
        standort={standortDialogEntity}
        setStandort={setStandortDialogEntity}
        isOpen={isStandortDialogOpen}
        closeDialog={() => {
          setStandortDialogEntity(undefined);
          setIsStandortDialogOpen(false);
          setIsAddingNewItem(false);
        }}
        isAddingNewItem={isAddingNewItem}
        setIsAddingNewItem={setIsAddingNewItem}
        reloadData={async () => {
          await loadStandortData(0);
        }}
      ></AdminManagementStandortDialog>
      <AdminManagmentWartelisteDialog
        warteliste={wartelisteDialogEntity}
        isOpen={wartelisteDialogEntity !== undefined}
        closeDialog={() => {
          setWartelisteDialogEntity(undefined);
          setIsAddingNewItem(false);
        }}
      ></AdminManagmentWartelisteDialog>
      <AdminManagementKursDialog
        allStandortNamen={allStandortNamen}
        kurs={kursDialogEntity}
        setKurs={setKursDialogEntity}
        isOpen={kursDialogEntity !== undefined}
        closeDialog={() => {
          setKursDialogEntity(undefined);
          setIsAddingNewItem(false);
        }}
        isAddingNewItem={isAddingNewItem}
        setIsAddingNewItem={setIsAddingNewItem}
        reloadData={async () => {
          await loadStandortData(0);
        }}
      ></AdminManagementKursDialog>
      <AdminInkassoDialog
        isOpen={isInkassoDialogOpen}
        onClose={() => {
          reloadMahnungData();
          setIsInkassoDialogOpen(false);
        }}
      ></AdminInkassoDialog>
      <SnackbarMessage
        open={snackBarCtx.isOpen}
        text={snackBarCtx.text}
        messageType={snackBarCtx.messageType}
        onCloseFct={snackBarCtx.closeSnackbar}
      ></SnackbarMessage>
    </ThemeProvider>
  );
};
export default AdminManagementPage;
